import { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { DayValue, Day } from 'react-modern-calendar-datepicker';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import { t } from 'i18next';
import { ReactComponent as PdfIcon } from '@/assets/img/v2/pdf-icon.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/v2/delete.red-icon.svg';

import { useDeleteDocument, useUploadDocument } from '../api/addPetInsurance';
import Utils from '@/utils/util';
import DatePickerWithPortal from './DatepickerWithPortal';
import { Loader } from '@/components/Loader/Loader';
import { showNotification } from '@/utils/notificationUtils';

import { ReactComponent as UploadFileIcon } from '@/assets/img/v2/upload-file-icon.svg';
import './VaccinationCard.scss';
import { FileDetails } from '../api/addPetVaccination';
import ConfirmationDialogV2 from '@/components/ConfirmationDialog/ConfirmationDialogV2';
import WebcamModal from '@/components/WebCam/WebCam';

export type Vaccine = {
  taken: boolean;
  takenDate: string;
  expiryDate: string;
  type: string;
  fileDetails?: FileDetails | null;
};
type VaccinationCardProps = {
  title: string;
  description: string;
  onChange: (vaccine: Vaccine) => void;
  data: Vaccine;
  petId: number;
};
export const VaccinationCard = ({
  title,
  description,
  onChange,
  data,
  petId,
}: VaccinationCardProps) => {
  const uploadPetDocumentMutation = useUploadDocument();
  const deletePetDocumentMutation = useDeleteDocument();

  const [day, setDay] = useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  const [vaccineTaken, setVaccineTaken] = useState<boolean>(false);
  const [vaccineTakenDate, setVaccineTakenDate] = useState<string>('');
  const [vaccineExpiryDate, setVaccineExpiryDate] = useState<string>('');
  const [fileDetails, setFileDetails] = useState<FileDetails | null>();
  useEffect(() => {
    setVaccineTaken(data.taken ? true : false);
    setVaccineTakenDate(data.takenDate);
    setVaccineExpiryDate(data.expiryDate);
    setFileDetails(data.fileDetails);
    console.log('VaccinationCard data', data);
    
  }, []);

  useEffect(() => {
    onDataChange();
  }, [vaccineTaken, vaccineTakenDate, vaccineExpiryDate, fileDetails]);

  const onRadioChange = (value: string) => {
    setVaccineTaken(value == 'true' ? true : false);
    if (value == 'false') {
      setVaccineTakenDate('');
    }
  };
  const onDateChange = (value: string, dateType: string) => {
    dateType === Utils.VACCINE_DATES.TAKEN_DATE
      ? setVaccineTakenDate(value)
      : setVaccineExpiryDate(value);
  };
  const onDataChange = () => {
    data.taken = vaccineTaken;
    data.takenDate = vaccineTakenDate;
    data.expiryDate = vaccineExpiryDate;
    data.fileDetails = fileDetails;
    onChange({ ...data });
  };
  const onFileSelect = (file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

    if (!validTypes.includes(file.type)) {
      showNotification({
        type: 'info',
        title: 'Only png, jpg images or pdf file is allowed',
      });
      return false;
    }

    if (file.size > 10000000) {
      showNotification({
        type: 'info',
        title: t('Uploaded image should be less than 5 mb .'),
      });
      return false;
    }
    uploadPetDocuments(file);
  };
  const handleCapture = (file: File) => {
    onFileSelect(file);
  };
  const uploadPetDocuments = async (selectedFiles: any) => {
    if (selectedFiles) {
      const formData = new FormData();
      formData.append('file', selectedFiles);
      formData.append('type', data.type);
      formData.append('petId', '' + petId);
      const response = await uploadPetDocumentMutation.mutateAsync({
        file: formData,
        onlyDocument: true,
      });
      setFileDetails({
        fileUrl: response.fileUrl,
        fileName: response.fileName,
        fileSize: response.fileSize,
      });
    }
  };
  const onConfirmDeleteDoc = () => {
    onDelete();
  };
  const onDelete =async () => {
    await deletePetDocumentMutation.mutateAsync({
      data: {
        id: fileDetails?.fileUrl ? fileDetails.fileUrl.split(/\//)[3] : '',
        file: "",
        type: ""
      },
      onlyDocument: true
    });
    setFileDetails(null);
    setShowDeleteConfirmation(false);
  };
  return (
    <div className="vaccination-card">
      <div className="vaccination-card-top">
        <div className="vaccination-desc">
          <h5>{title}</h5>
          <p>{description}</p>
        </div>
        <div className="petsked-form vaccination-details">
          <div className="options">
            <Form.Check
              label="Taken"
              inline
              name={data.type}
              type="radio"
              checked={vaccineTaken}
              value={'true'}
              className="form-check-toggle"
              onChange={(e: any) => onRadioChange(e.target.value)}
            />
            <Form.Check
              label="Not Taken"
              inline
              name={data.type}
              type="radio"
              checked={!vaccineTaken}
              value={'false'}
              className="form-check-toggle"
              onChange={(e: any) => onRadioChange(e.target.value)}
            />
          </div>
        </div>
      </div>
      {vaccineTaken && (
        <>
          <div className="split">
            <div className="form-field">
              <label className="form-label">Taken On</label>
              <DatePickerWithPortal
                value={vaccineTakenDate ? vaccineTakenDate : undefined}
                onChange={(val: DayValue) => {
                  onDateChange(
                    `${val?.month}/${val?.day}/${val?.year}`,
                    Utils.VACCINE_DATES.TAKEN_DATE
                  );
                }}
                placeholder={t('Taken date')}
                maximumDate={day}
              />
            </div>
            <div className="form-field">
              <label className="form-label">Expiry On</label>
              <DatePickerWithPortal
                value={vaccineExpiryDate ? vaccineExpiryDate : undefined}
                onChange={(val: DayValue) => {
                  onDateChange(
                    `${val?.month}/${val?.day}/${val?.year}`,
                    Utils.VACCINE_DATES.EXPIRY_DATE
                  );
                }}
                placeholder={t('Expiry date')}
              />
            </div>
          </div>
          {!fileDetails ? (
            <>
            <FileUploader handleChange={onFileSelect} name="file">
              <div className="file-upload">
                {uploadPetDocumentMutation.isLoading ? (
                  <Loader text="uploading..." />
                ) : (
                  <>
                    <UploadFileIcon />
                    <p>
                      {t('Drag & drop or')} <span>{t('choose files')}</span> {t('to upload')}
                    </p>
                    <p>
                      {t('Only')} <b>{t('PDF, JPEG, JPG, PNG')}</b> {t('files of max')} <b>{t('10MB')}</b>{' '}
                      {t('size allowed')}
                    </p>
                  </>
                )}
              </div>
            </FileUploader>
            {Utils.isMobile() ? (
              <></>
            ) : (
              !uploadPetDocumentMutation.isLoading && (
                <WebcamModal  onCapture={handleCapture} />
              )
            )}
            </>
          ) : (
            <div className="vaccination-card">
              <div className="left-details">
                <PdfIcon />
                <div className="file-details">
                  <p>
                    <a href={fileDetails?.fileUrl} target="_blank" rel="noreferrer">
                      {fileDetails?.fileName ? fileDetails.fileName : 'Vaccine Document'}
                    </a>
                  </p>
                  <p>{Utils.getFileSize(fileDetails?.fileSize, 2)}</p>
                </div>
              </div>
              <div className="right-action">
                <DeleteIcon onClick={()=>setShowDeleteConfirmation(true)} />
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmationDialogV2
        title={t('Remove Vaccination Document?')}
        description={t('Do you really want to remove the document?')}
        yesButtonLabel={t('Yes, Confirm')}
        noButtonLabel={t('No, Not Now')}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={onConfirmDeleteDoc}
        open={showDeleteConfirmation}
      >
        {<></>}
      </ConfirmationDialogV2>
    </div>
  );
};
